import React, { useMemo, useState } from 'react';
import { Select } from 'antd';
import momentTimezone from 'moment-timezone';
import ActionBar from '../../../components/common/ActionBar';
import ContactHelpText from '../../../components/common/ContactHelpText';
import InlineAlert from '../../../components/common/InlineAlert';
import Note from '../../../components/common/Note';
import TimeslotGrid from '../../../components/NewBooking/TimeslotGrid';
import {
  Card,
  CardBody,
  PageHead,
  PageSubtitle,
  PageTitle,
  SectionTitle,
} from '../../../components/common/Card';
import {
  DialogBody,
  DialogFooter,
  DialogLede,
  DialogModal,
  DialogPrimaryButton,
  DialogTitle,
  GhostButton,
} from '../../../components/common/DialogModal';
import AppointmentCalendar from '../../NewBooking/SelectDate/BookTimePicker/AppointmentCalendar';
import {
  Field,
  FieldHint,
  FieldLabel,
  RequiredMark,
  TimezoneSelect,
} from '../../NewBooking/SelectDate/BookTimePicker/styles';
import { AUS_TIMEZONE_LIST_ONLY_LABEL } from '../../../constants/timezone';
import { TIME_FORMAT } from '../../../constants/time';
import { formatAppointmentMomentLabel } from '../../../utils/appointmentDisplayUtils';
import { buildTimeslotOptions } from '../../../utils/timeslotUtils';
import {
  isInsideCancellationWindow,
  withoutCurrentStart,
} from '../../../utils/rescheduleUtils';
import CurrentAppointment from './CurrentAppointment';

const { DATE_AND_TIME } = TIME_FORMAT;

const NO_DATE_SELECTED_MESSAGE =
  'Select your preferred appointment time — pick a date above first.';
const NO_TIMESLOT_MESSAGE =
  'There are no appointment times left on this date. Please choose another date.';
const SELECT_DATE_MESSAGE = 'Please select appointment date.';
const SELECT_TIME_MESSAGE = 'Please select appointment time.';

const SelectNewTime = ({
  workflow,
  contact,
  durationMinutes,
  timezone,
  onTimezoneChange,
  bookingTime,
  dispatchBookingTime,
  availableTimeList,
  isScheduleLoaded,
  scheduleError,
  errorMessage,
  onBack,
  onNext,
}) => {
  const [validationMessage, setValidationMessage] = useState('');
  const [isWindowWarningOpen, setIsWindowWarningOpen] = useState(false);

  const currentStartDateTime = workflow?.appointment?.startDateTime;
  const timeslots = useMemo(
    () =>
      withoutCurrentStart(
        buildTimeslotOptions({
          bookingDate: bookingTime.date,
          appointmentLength: durationMinutes,
          availableTimeList,
          timezone,
        }),
        currentStartDateTime,
        timezone
      ),
    [
      bookingTime.date,
      durationMinutes,
      availableTimeList,
      timezone,
      currentStartDateTime,
    ]
  );

  const selectedTimeslot = bookingTime.time?.start
    ? momentTimezone(bookingTime.time.start).tz(timezone).format(DATE_AND_TIME)
    : '';

  const handleSelectTimeslot = (timeslot) => {
    const startMoment = momentTimezone.tz(timeslot, DATE_AND_TIME, timezone);
    dispatchBookingTime({
      type: 'updateTime',
      time: {
        start: startMoment.format(),
        end: startMoment.clone().add(durationMinutes, 'minutes').format(),
      },
    });
    setValidationMessage('');
  };

  const handleNext = () => {
    if (!bookingTime.date) {
      setValidationMessage(SELECT_DATE_MESSAGE);
      return;
    }
    if (!bookingTime.time?.start) {
      setValidationMessage(SELECT_TIME_MESSAGE);
      return;
    }
    if (isInsideCancellationWindow(bookingTime.time.start)) {
      setIsWindowWarningOpen(true);
      return;
    }
    onNext();
  };

  const specialistName = [
    workflow?.specialist?.firstName,
    workflow?.specialist?.lastName,
  ]
    .filter(Boolean)
    .join(' ');
  const hasNoAvailabilityThisMonth =
    isScheduleLoaded && !scheduleError && availableTimeList.length === 0;
  const selectedLabel = bookingTime.time?.start
    ? formatAppointmentMomentLabel(
        momentTimezone(bookingTime.time.start).tz(timezone)
      )
    : '';

  return (
    <>
      <PageHead>
        <PageTitle>Reschedule your appointment</PageTitle>
        <PageSubtitle>
          Choose a new time with the same clinician. Your appointment is not
          moved until you confirm.
        </PageSubtitle>
      </PageHead>

      {/* From the server (e.g. the time was just taken), so above the fold. */}
      {errorMessage && <Note tone="warning">{errorMessage}</Note>}

      <CurrentAppointment workflow={workflow} timezone={timezone} />

      <Card>
        <CardBody>
          <SectionTitle>Select your preferred appointment date</SectionTitle>
          <AppointmentCalendar
            availableTimeList={availableTimeList}
            bookingTime={bookingTime}
            timezone={timezone}
            dispatch={dispatchBookingTime}
          />
          {hasNoAvailabilityThisMonth && (
            <Note tone="warning">
              {`Dr. ${specialistName} has no available appointments this month. Try the next month, or call ${contact.phone} and we'll find you a time.`}
            </Note>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <SectionTitle>Please select a Date / Time</SectionTitle>

          <Field>
            <FieldLabel htmlFor="reschedule-timezone">
              <RequiredMark>*</RequiredMark>Timezone
            </FieldLabel>
            <FieldHint>
              Set from your postcode &mdash; change it if you&rsquo;ll be
              somewhere else at your appointment time.
            </FieldHint>
            <TimezoneSelect
              id="reschedule-timezone"
              placeholder="Select your time zone"
              value={timezone}
              onSelect={onTimezoneChange}
            >
              {AUS_TIMEZONE_LIST_ONLY_LABEL.map((zone) => (
                <Select.Option value={zone} key={zone}>
                  {zone}
                </Select.Option>
              ))}
            </TimezoneSelect>
          </Field>

          <Field>
            <FieldLabel>
              <RequiredMark>*</RequiredMark>
              {`Appointment Time (${durationMinutes} minutes)`}
            </FieldLabel>
            <TimeslotGrid
              key={bookingTime.date}
              timeslots={timeslots}
              selectedTimeslot={selectedTimeslot}
              onSelect={handleSelectTimeslot}
              emptyMessage={
                bookingTime.date
                  ? NO_TIMESLOT_MESSAGE
                  : NO_DATE_SELECTED_MESSAGE
              }
            />
          </Field>

          {validationMessage && <InlineAlert>{validationMessage}</InlineAlert>}
          {scheduleError && <InlineAlert>{scheduleError}</InlineAlert>}
        </CardBody>
      </Card>

      <ContactHelpText phone={contact.phone} />

      <ActionBar
        backLabel="Back"
        onBack={onBack}
        nextLabel="Next"
        onNext={handleNext}
      />

      <DialogModal
        visible={isWindowWarningOpen}
        footer={null}
        width={520}
        onCancel={() => setIsWindowWarningOpen(false)}
      >
        <DialogBody>
          <DialogTitle>This time is less than 2 business days away</DialogTitle>
          <DialogLede>{selectedLabel}</DialogLede>
          <DialogLede>
            You can still choose it. But once it is confirmed, missed
            appointments, or appointments cancelled without valid medical reason
            within 2 business days of the appointment will incur a fee ($150).
            You would need to call us rather than change it here.
          </DialogLede>
        </DialogBody>
        <DialogFooter>
          <GhostButton
            type="text"
            onClick={() => setIsWindowWarningOpen(false)}
          >
            Choose another time
          </GhostButton>
          <DialogPrimaryButton
            onClick={() => {
              setIsWindowWarningOpen(false);
              onNext();
            }}
          >
            Use this time
          </DialogPrimaryButton>
        </DialogFooter>
      </DialogModal>
    </>
  );
};

export default SelectNewTime;
